import React, { useMemo, useState } from "react";
import { doc, setDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import { colors, radius, spacing, typography, cardBase, shadows } from "../../utils/designTokens";
import { toISODateString } from "../../utils/dates";
import { datosEdificio } from "../../utils/datosEdificio";
import { computeBloqueadoHasta } from "../../utils/crmAsignacion";
import { getStatusStyle, STATUS_LABELS } from "../../utils/statusColors";
import { useIsMobile } from "../../hooks/useResponsive";
import { useProspeccionData } from "../../hooks/useProspeccionData";
import { useProspeccionFilters } from "../../context/ProspeccionFilterContext";
import { useToast } from "../../components/Toast";
import StatusBadge from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import CrmFicha from "../../components/CrmFicha";
import {
  FaRegClock, FaArrowRight, FaRegStickyNote, FaCheck,
  FaListUl, FaRegCalendarAlt, FaChevronLeft, FaChevronRight,
} from "react-icons/fa";

const fmt = (n) => (n == null || n === "" ? "—" : Number(n).toLocaleString("es-UY"));
// Etapas del embudo (en orden). perdido/descartado se muestran aparte.
const PIPELINE = ["sin_contactar", "contactado", "en_conversacion", "reunion", "propuesta", "ganado"];
const CERRADOS = ["perdido", "descartado"];

function scoreColor(v) {
  const n = Number(v) || 0;
  if (n >= 80) return colors.success;
  if (n >= 60) return colors.primary;
  if (n >= 40) return colors.warning;
  return colors.gray600;
}

/**
 * Inicio del Comercial: KPIs propios + pipeline (embudo) clickable que filtra la
 * lista y el mapa, y las próximas acciones ordenadas por fecha (padrón, unidades,
 * score) que abren la ficha CRM.
 */
export default function HomeComercial({ setSelected }) {
  const isMobile = useIsMobile();
  const { showToast } = useToast();
  const { puntos, crmByPadron, comerciales, loading, updateCrm } = useProspeccionData();
  const { soloCrm } = useProspeccionFilters();
  const [ficha, setFicha] = useState(null);
  const [vistaAcc, setVistaAcc] = useState("semana"); // semana | lista

  const email = (auth.currentUser?.email || "").toLowerCase();
  const puntoByPadron = useMemo(() => {
    const m = {}; for (const p of puntos) m[String(p.padron)] = p; return m;
  }, [puntos]);

  const crmList = useMemo(() => Object.values(crmByPadron), [crmByPadron]);

  // "Míos": SOLO los prospectos asignados a mi email. Sin fallback al pipeline
  // compartido: los accionables de otro comercial no aparecen en mi Inicio
  // (lo de los demás se ve en Prospección/Mapa, con su candado).
  const mios = useMemo(() => {
    if (!email) return [];
    return crmList.filter((c) => String(c.asignadoEmail || "").toLowerCase() === email);
  }, [crmList, email]);

  const countByEstado = useMemo(() => {
    const acc = {};
    for (const c of mios) { const e = c.estado || "sin_contactar"; acc[e] = (acc[e] || 0) + 1; }
    return acc;
  }, [mios]);

  const maxPipe = Math.max(1, ...PIPELINE.map((e) => countByEstado[e] || 0));

  // Buckets por urgencia. proximaAccionFecha es "YYYY-MM-DD": la comparación
  // lexicográfica es cronológica y el "hoy" se arma en hora local (UTC-3),
  // nunca con toISOString() que saltaría de día después de las 21:00.
  const acciones = useMemo(() => {
    const hoyStr = toISODateString(new Date());
    const byFecha = (a, b) => String(a.proximaAccionFecha || "9999-99-99").localeCompare(String(b.proximaAccionFecha || "9999-99-99"));
    const conAccion = mios
      .filter((c) => c.proximaAccion || c.proximaAccionFecha)
      .map((c) => ({ ...c, punto: puntoByPadron[String(c.padron)] || null }));
    return {
      vencidas: conAccion.filter((c) => c.proximaAccionFecha && c.proximaAccionFecha < hoyStr).sort(byFecha),
      hoy: conAccion.filter((c) => c.proximaAccionFecha === hoyStr),
      futuras: conAccion.filter((c) => !c.proximaAccionFecha || c.proximaAccionFecha > hoyStr).sort(byFecha),
      total: conAccion.length,
    };
  }, [mios, puntoByPadron]);

  function abrirEstado(estado) {
    soloCrm(estado);
    setSelected("prospeccion");
  }
  function abrirFicha(c) {
    const p = puntoByPadron[String(c.padron)] || { padron: c.padron, direccion: c.direccion, score: c.score };
    setFicha(p);
  }

  // "Cuando lo gestioné, sale" (estilo Microsoft To Do): completar una acción la
  // saca del calendario/lista, deja registro como nota de gestión y empuja el
  // reloj de 60 días (una nota ES gestión, mismo criterio que esGestion()).
  async function completarAccion(c) {
    try {
      const nowTs = Timestamp.now();
      const nota = {
        texto: `Hecho: ${c.proximaAccion || "próxima acción"}`,
        autor: auth.currentUser?.email || "comercial",
        at: new Date().toISOString(),
      };
      const payload = {
        padron: Number(c.padron),
        proximaAccion: "", proximaAccionFecha: "", proximaAccionHora: "",
        notas: [...(Array.isArray(c.notas) ? c.notas : []), nota],
        ultimaGestionAt: nowTs,
        bloqueadoHasta: computeBloqueadoHasta(nowTs.toDate()),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email || "comercial",
      };
      await setDoc(doc(db, "prospectos_crm", String(c.padron)), payload, { merge: true });
      updateCrm(c.padron, payload);
      showToast("Acción completada — quedó como nota de gestión.", "success");
    } catch (e) {
      showToast(e.message || "No se pudo completar la acción.", "error");
    }
  }

  return (
    <div style={{ padding: isMobile ? spacing[4] : spacing[6], maxWidth: 1280, margin: "0 auto" }}>
      {/* Pipeline / embudo — solo en vista Lista: en Semana el calendario ocupa todo */}
      {vistaAcc === "lista" && (
      <div style={{ ...cardBase, padding: isMobile ? spacing[3] : spacing[4], marginBottom: isMobile ? spacing[4] : spacing[5] }}>
        <div style={{ fontSize: isMobile ? typography.sizes.base : typography.sizes.lg, color: colors.gray900, marginBottom: isMobile ? spacing[2] : spacing[3] }}>Pipeline comercial</div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : `repeat(${PIPELINE.length}, 1fr)`, gap: spacing[2] }}>
          {PIPELINE.map((e) => {
            const n = countByEstado[e] || 0;
            const st = getStatusStyle(e);
            return (
              <button key={e} onClick={() => abrirEstado(e)} style={{ ...stageBtn, padding: isMobile ? "6px 4px" : `${spacing[3]}px ${spacing[2]}px`, borderColor: st.border, background: st.bg }} title={`Ver ${STATUS_LABELS[e]}`}>
                <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 400, color: st.text }}>{n}</div>
                <div style={{ fontSize: isMobile ? 9 : typography.sizes.xs, color: st.text, lineHeight: 1.15 }}>{STATUS_LABELS[e]}</div>
                {!isMobile && <div style={{ height: 4, borderRadius: 2, marginTop: 6, background: st.text, opacity: 0.25, width: `${Math.round((n / maxPipe) * 100)}%`, minWidth: n ? 8 : 0 }} />}
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: spacing[2], marginTop: spacing[3], flexWrap: "wrap" }}>
          {CERRADOS.map((e) => (
            <button key={e} onClick={() => abrirEstado(e)} style={{ ...chipBtn }}>
              <StatusBadge status={e} size="sm" /> <span style={{ color: colors.gray600 }}>{countByEstado[e] || 0}</span>
            </button>
          ))}
        </div>
      </div>
      )}

      {/* Agenda — semana estilo To Do (principal) o lista por urgencia */}
      <div style={{ marginBottom: spacing[3], display: "flex", alignItems: "center", gap: spacing[2] }}>
        <FaRegClock size={14} color={colors.gray400} />
        <span style={{ fontSize: typography.sizes.lg, color: colors.gray900, flex: 1 }}>Agenda</span>
        <VistaBtn activo={vistaAcc === "semana"} onClick={() => setVistaAcc("semana")} icon={FaRegCalendarAlt} label="Semana" />
        <VistaBtn activo={vistaAcc === "lista"} onClick={() => setVistaAcc("lista")} icon={FaListUl} label="Lista" />
      </div>
      {loading ? (
        <div style={{ textAlign: "center", padding: spacing[6], color: colors.gray600 }}>Cargando…</div>
      ) : acciones.total === 0 ? (
        <EmptyState icon={FaRegStickyNote} title="Sin acciones pendientes" description="Abrí un edificio, cargá su ficha CRM y ponele una próxima acción con fecha." />
      ) : vistaAcc === "semana" ? (
        <SemanaAcciones acciones={acciones} onOpen={abrirFicha} onDone={completarAccion} isMobile={isMobile} />
      ) : (
        <div style={{ display: "grid", gap: spacing[2] }}>
          {[
            { key: "vencidas", titulo: "Atrasadas", items: acciones.vencidas, headerColor: colors.errorDark },
            { key: "hoy", titulo: "Hoy", items: acciones.hoy, headerColor: colors.primary },
            { key: "futuras", titulo: "Próximas", items: acciones.futuras, headerColor: colors.gray600 },
          ].map(({ key, titulo, items, headerColor }) => items.length > 0 && (
            <React.Fragment key={key}>
              <div style={{ fontSize: typography.sizes.xs, textTransform: "uppercase", letterSpacing: "0.5px", color: headerColor, marginTop: spacing[1] }}>
                {titulo} ({items.length})
              </div>
              {items.map((c) => <AccionRow key={c.padron} c={c} bucket={key} onOpen={() => abrirFicha(c)} onDone={() => completarAccion(c)} />)}
            </React.Fragment>
          ))}
        </div>
      )}

      <CrmFicha
        open={!!ficha}
        onClose={() => setFicha(null)}
        padron={ficha?.padron}
        punto={ficha}
        crm={ficha ? crmByPadron[String(ficha.padron)] : null}
        comerciales={comerciales}
        onSaved={updateCrm}
        setSelected={setSelected}
      />
    </div>
  );
}

function VistaBtn({ activo, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick} aria-pressed={activo}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px",
        border: `1px solid ${activo ? colors.primary : colors.gray200}`, borderRadius: radius.sm,
        background: activo ? colors.primaryLighter : colors.white, color: activo ? colors.primary : colors.gray700,
        cursor: "pointer", fontSize: typography.sizes.sm, fontFamily: "inherit", transition: "all 0.15s ease",
      }}
    >
      <Icon size={11} /> {label}
    </button>
  );
}

/**
 * Agenda semanal estilo Microsoft To Do: 7 columnas (desktop) o selector de día
 * (mobile). Las acciones SIN hora van al inicio del día y las con hora después,
 * ordenadas. Las atrasadas y las sin fecha viven en sus franjas arriba (no en
 * las columnas). El círculo marca la acción como hecha: sale de la agenda y
 * queda registrada como nota de gestión.
 */
function SemanaAcciones({ acciones, onOpen, onDone, isMobile }) {
  const hoyStr = toISODateString(new Date());
  const lunesDe = (base) => { const x = new Date(base); x.setHours(12, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
  const [lunes, setLunes] = useState(() => lunesDe(new Date()));
  const [diaSel, setDiaSel] = useState(hoyStr);

  const dias = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(lunes); d.setDate(lunes.getDate() + i);
    return { fecha: toISODateString(d), date: d };
  }), [lunes]);

  // TODA acción con fecha aparece en su día (las atrasadas, en rojo). La franja
  // "Atrasadas" muestra solo las que quedan FUERA de la semana visible, así al
  // navegar a una semana vieja las tareas están en sus columnas y no duplicadas.
  const porFecha = useMemo(() => {
    const acc = {};
    for (const c of [...acciones.vencidas, ...acciones.hoy, ...acciones.futuras]) {
      if (!c.proximaAccionFecha) continue;
      (acc[c.proximaAccionFecha] = acc[c.proximaAccionFecha] || []).push(c);
    }
    // Sin hora primero, con hora en orden ("" ordena antes que "08:00").
    for (const k of Object.keys(acc)) acc[k].sort((a, b) => String(a.proximaAccionHora || "").localeCompare(String(b.proximaAccionHora || "")));
    return acc;
  }, [acciones]);
  const sinFecha = useMemo(() => [...acciones.hoy, ...acciones.futuras].filter((c) => !c.proximaAccionFecha), [acciones]);
  const atrasadasFuera = useMemo(
    () => acciones.vencidas.filter((c) => c.proximaAccionFecha < toISODateString(dias[0].date) || c.proximaAccionFecha > toISODateString(dias[6].date)),
    [acciones.vencidas, dias]
  );

  const rango = `${dias[0].date.toLocaleDateString("es-UY", { day: "numeric", month: "short" })} – ${dias[6].date.toLocaleDateString("es-UY", { day: "numeric", month: "short", year: "numeric" })}`;
  const moverSemana = (dir) => setLunes((l) => { const x = new Date(l); x.setDate(x.getDate() + dir * 7); return x; });
  const irHoy = () => { setLunes(lunesDe(new Date())); setDiaSel(hoyStr); };
  const nombreDia = (d) => d.toLocaleDateString("es-UY", { weekday: "short" }).replace(".", "");

  const franja = (titulo, items, color) => items.length > 0 && (
    <div style={{ marginBottom: spacing[3] }}>
      <div style={{ fontSize: typography.sizes.xs, textTransform: "uppercase", letterSpacing: "0.5px", color, marginBottom: spacing[2] }}>
        {titulo} ({items.length})
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(240px, 1fr))", gap: spacing[2] }}>
        {items.map((c) => <TareaCard key={c.padron} c={c} conFecha onOpen={() => onOpen(c)} onDone={() => onDone(c)} />)}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: spacing[2], marginBottom: spacing[3] }}>
        <button aria-label="Semana anterior" onClick={() => moverSemana(-1)} style={calNavBtn}><FaChevronLeft size={11} /></button>
        <button onClick={irHoy} style={{ ...calNavBtn, width: "auto", padding: "0 12px", fontSize: typography.sizes.sm, fontFamily: "inherit" }}>Hoy</button>
        <button aria-label="Semana siguiente" onClick={() => moverSemana(1)} style={calNavBtn}><FaChevronRight size={11} /></button>
        <span style={{ fontSize: typography.sizes.base, color: colors.gray700, textTransform: "capitalize" }}>{rango}</span>
      </div>

      {franja("Atrasadas de otras semanas", atrasadasFuera, colors.errorDark)}
      {franja("Sin fecha", sinFecha, colors.gray600)}

      {isMobile ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 4, marginBottom: spacing[3] }}>
            {dias.map(({ fecha, date }) => {
              const n = (porFecha[fecha] || []).length;
              const sel = fecha === diaSel;
              const esHoy = fecha === hoyStr;
              return (
                <button key={fecha} onClick={() => setDiaSel(fecha)} aria-label={`${nombreDia(date)} ${date.getDate()}${n ? `, ${n} ${n === 1 ? "acción" : "acciones"}` : ""}`}
                  style={{
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "6px 0",
                    border: `1px solid ${sel ? colors.primary : colors.gray200}`, borderRadius: radius.sm,
                    background: sel ? colors.primarySubtle : colors.white, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s ease",
                  }}>
                  <span style={{ fontSize: 10, textTransform: "uppercase", color: colors.gray600 }}>{nombreDia(date)}</span>
                  <span style={{ fontSize: typography.sizes.base, lineHeight: "20px", width: 20, height: 20, borderRadius: radius.full, background: esHoy ? colors.primary : "transparent", color: esHoy ? colors.white : colors.gray900 }}>{date.getDate()}</span>
                  <span style={{ fontSize: 10, minHeight: 13, color: n ? ((porFecha[fecha] || []).some((c) => c.proximaAccionFecha < hoyStr) ? colors.error : colors.primary) : "transparent" }}>{n || "·"}</span>
                </button>
              );
            })}
          </div>
          <div style={{ display: "grid", gap: spacing[2] }}>
            {(porFecha[diaSel] || []).length === 0 ? (
              <div style={{ ...cardBase, padding: spacing[4], textAlign: "center", color: colors.gray600, fontSize: typography.sizes.sm }}>Nada agendado para este día.</div>
            ) : (
              (porFecha[diaSel] || []).map((c) => <TareaCard key={c.padron} c={c} onOpen={() => onOpen(c)} onDone={() => onDone(c)} />)
            )}
          </div>
        </>
      ) : (
        // minmax(0,1fr): sin esto, un texto con nowrap fuerza el ancho de la
        // columna y desborda sobre el día siguiente.
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: spacing[2], alignItems: "stretch" }}>
          {dias.map(({ fecha, date }) => {
            const items = porFecha[fecha] || [];
            const esHoy = fecha === hoyStr;
            const pasado = fecha < hoyStr;
            return (
              <div key={fecha} style={{
                minWidth: 0, minHeight: 180, display: "flex", flexDirection: "column",
                background: colors.white, borderRadius: radius.md, boxShadow: shadows.sm,
                padding: spacing[2], opacity: pasado && items.length === 0 ? 0.6 : 1,
                outline: esHoy ? `2px solid ${colors.primary}` : "none", outlineOffset: -2,
              }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 5, padding: "2px 2px 5px", marginBottom: spacing[1], borderBottom: `1px solid ${colors.gray100}` }}>
                  <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px", color: esHoy ? colors.primary : colors.gray600 }}>{nombreDia(date)}</span>
                  <span style={{ fontSize: typography.sizes.md, color: esHoy ? colors.primary : colors.gray900 }}>{date.getDate()}</span>
                  {esHoy && <span style={{ fontSize: 10, color: colors.primary }}>· hoy</span>}
                </div>
                <div style={{ display: "grid", gap: spacing[1], alignContent: "start", flex: 1 }}>
                  {items.length === 0 ? (
                    <span style={{ fontSize: typography.sizes.xs, color: colors.gray300, textAlign: "center", paddingTop: spacing[3] }}>—</span>
                  ) : (
                    items.map((c) => <TareaCard key={c.padron} c={c} tinted onOpen={() => onOpen(c)} onDone={() => onDone(c)} />)
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Tarjeta de acción de la agenda: check para completarla, hora (si tiene), edificio, acción y estado.
 *  `tinted` = dentro de una columna blanca (fondo gris, sin sombra); atrasada = borde rojo. */
function TareaCard({ c, conFecha, tinted, onOpen, onDone }) {
  const ef = datosEdificio(c.punto, c);
  const atrasada = !!c.proximaAccionFecha && c.proximaAccionFecha < toISODateString(new Date());
  return (
    // minWidth 0 + overflow hidden: como ítem de grid/flex, sin esto la tarjeta
    // no encoge por debajo de su contenido y se derrama sobre la columna vecina.
    <div style={{
      minWidth: 0, overflow: "hidden",
      background: tinted ? colors.gray50 : colors.white, borderRadius: radius.sm,
      boxShadow: tinted ? "none" : shadows.sm, padding: spacing[2],
      display: "flex", gap: spacing[2], alignItems: "flex-start",
      border: tinted ? `1px solid ${colors.gray100}` : "1px solid transparent",
      borderLeft: atrasada ? `3px solid ${colors.error}` : "3px solid transparent",
    }}>
      <button
        onClick={(e) => { e.stopPropagation(); onDone(); }}
        aria-label={`Marcar como hecha: ${c.proximaAccion || ef.direccion || c.padron}`}
        title="Marcar como hecha"
        style={doneBtn}
        className="habitia-btn-ghost"
      >
        <FaCheck size={8} />
      </button>
      <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={onOpen} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen()}>
        {(c.proximaAccionHora || (conFecha && c.proximaAccionFecha)) && (
          <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap", marginBottom: 2 }}>
            {conFecha && c.proximaAccionFecha && (
              <span style={{ fontSize: 10, color: colors.errorDark, background: colors.errorLight, borderRadius: radius.full, padding: "1px 6px" }}>
                {c.proximaAccionFecha.split("-").reverse().slice(0, 2).join("/")}
              </span>
            )}
            {c.proximaAccionHora && (
              <span style={{ fontSize: 10, color: colors.primary, background: colors.primaryLighter, borderRadius: radius.full, padding: "1px 6px" }}>
                {c.proximaAccionHora}
              </span>
            )}
          </div>
        )}
        <div style={{ fontSize: typography.sizes.xs, color: colors.gray900, lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word" }}>
          {ef.direccion || `Padrón ${c.padron}`}
        </div>
        {c.proximaAccion && (
          <div style={{ fontSize: typography.sizes.xs, color: colors.gray700, lineHeight: 1.35, marginTop: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word" }}>
            {c.proximaAccion}
          </div>
        )}
        <div style={{ display: "flex", gap: 5, alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
          {c.estado && <StatusBadge status={c.estado} size="sm" />}
          <span style={{ fontSize: 10, color: colors.gray400 }}>{fmt(ef.nUnidades)} unid.</span>
        </div>
      </div>
    </div>
  );
}

const doneBtn = {
  width: 18, height: 18, marginTop: 3, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
  border: `1.5px solid ${colors.gray300}`, borderRadius: radius.full, background: colors.white, color: colors.gray300,
  cursor: "pointer", padding: 0, transition: "all 0.15s ease",
};

const calNavBtn = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30,
  border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, background: colors.white,
  color: colors.gray600, cursor: "pointer", transition: "all 0.15s ease",
};

function AccionRow({ c, bucket, onOpen, onDone }) {
  const ef = datosEdificio(c.punto, c);
  const hora = c.proximaAccionHora ? ` · ${c.proximaAccionHora}` : "";
  const chip = bucket === "vencidas"
    ? { label: `${c.proximaAccionFecha}${hora}`, bg: colors.errorLight, text: colors.errorDark }
    : bucket === "hoy"
      ? { label: `Hoy${hora}`, bg: colors.warningLight, text: colors.warningDark }
      : { label: `${c.proximaAccionFecha || "Sin fecha"}${hora}`, bg: colors.gray100, text: colors.gray600 };
  return (
    <div style={{ ...cardBase, padding: spacing[3], display: "flex", alignItems: "center", gap: spacing[3], cursor: "pointer" }}
      onClick={onOpen} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen()}>
      {onDone && (
        <button
          onClick={(e) => { e.stopPropagation(); onDone(); }}
          aria-label={`Marcar como hecha: ${c.proximaAccion || ef.direccion || c.padron}`}
          title="Marcar como hecha"
          style={doneBtn}
        >
          <FaCheck size={8} />
        </button>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: typography.sizes.base, color: colors.gray900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {ef.direccion || `Padrón ${c.padron}`}
        </div>
        <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
          <span>Padrón {c.padron}</span>
          <span>· {fmt(ef.nUnidades)} unid.</span>
          <span>· score <b style={{ color: scoreColor(c.punto?.score ?? c.score) }}>{c.punto?.score ?? c.score ?? "—"}</b></span>
        </div>
        {c.proximaAccion && <div style={{ fontSize: typography.sizes.sm, color: colors.gray700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>→ {c.proximaAccion}</div>}
      </div>
      {c.estado && <StatusBadge status={c.estado} size="sm" />}
      <span style={{ fontSize: typography.sizes.sm, color: chip.text, background: chip.bg, borderRadius: radius.full, padding: "2px 8px", whiteSpace: "nowrap" }}>{chip.label}</span>
      <FaArrowRight size={12} color={colors.gray400} />
    </div>
  );
}

const stageBtn = {
  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
  padding: `${spacing[3]}px ${spacing[2]}px`, borderRadius: radius.md, border: "1px solid",
  cursor: "pointer", textAlign: "center", fontFamily: "inherit",
};
const chipBtn = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: `${spacing[1]}px ${spacing[2]}px`,
  background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.full,
  cursor: "pointer", fontSize: typography.sizes.sm,
};
